import { CircleAlert, Clock3, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { money } from "../lib/format";
import { useKyc } from "../lib/queries";
import type { Wallet } from "../types";

/**
 * The wallet's verification notice: where the customer's Kuspit file stands, what the 3,000 UDI
 * monthly limit leaves them this month, and that a deposit over it is returned to the sending
 * account (Daniel, call of 2026-10-06: customers must not lose a transfer without an explanation).
 */
export function KycNotice({ wallet }: { wallet: Wallet }) {
  const kyc = useKyc({ enabled: wallet.status === "active" });
  if (wallet.status !== "active") return null;
  const file = kyc.data?.file;
  const limit = wallet.depositLimit;
  const pendingWithdrawals = !wallet.verification.withdrawalEligible;
  if (!limit.applies && !pendingWithdrawals) return null;

  const cap = limit.applies ? (
    <> Este mes puedes recibir hasta <span translate="no">{money.format(limit.limitMxn)}</span> (<span translate="no">{limit.limitUdi.toLocaleString("es-MX")} UDIs</span>) y te quedan <span translate="no">{money.format(limit.remainingMxn)}</span>; una transferencia que lo rebase la devuelve Kuspit a tu cuenta de origen.</>
  ) : null;
  const withdrawals = pendingWithdrawals ? " Los retiros se habilitan cuando Kuspit apruebe tu expediente." : "";

  if (file?.status === "submitted") {
    return (
      <div className="inline-notice info" role="status">
        <Clock3 size={17} aria-hidden="true" />
        <span><strong>Tu expediente está en revisión con Kuspit</strong> (suele tardar de 7 a 10 días).{withdrawals}{cap}</span>
      </div>
    );
  }
  if (file?.status === "rejected") {
    return (
      <div className="inline-notice warn" role="alert">
        <CircleAlert size={17} aria-hidden="true" />
        <span><strong>Kuspit pidió corregir tu expediente.</strong> {file.note ?? ""} <Link to="/verificacion/expediente">Corregir documentos</Link>.{cap}</span>
      </div>
    );
  }
  return (
    <div className="inline-notice warn" role="status">
      <ShieldCheck size={17} aria-hidden="true" />
      <span>
        <strong>Completa tu expediente con Kuspit</strong> para aportar sin límite.{withdrawals}{cap}{" "}
        <Link to="/verificacion/expediente">{file?.status === "in_progress" ? "Continuar expediente" : "Completar expediente"}</Link>
      </span>
    </div>
  );
}
